import { useState, Fragment } from 'react';
import { useAuditLog } from '../../hooks/useAuditLog.js';
import { fetchEntry } from '../../hooks/useTimesheet.js';
import EntryEditModal from '../../components/timesheet/EntryEditModal.jsx';
import { useError } from '../../components/shared/ErrorBanner.jsx';

const diffStyle = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 11,
  color: 'var(--on-surface-variant)',
  background: 'var(--surface-container)',
  padding: 10,
  margin: 0,
  whiteSpace: 'pre-wrap',
  maxHeight: 260,
  overflow: 'auto',
};

export default function AuditPage() {
  const [actionFilter, setActionFilter] = useState('');
  const { data: entries = [], refetch } = useAuditLog({ action: actionFilter || undefined });
  const [expanded, setExpanded] = useState(null);
  const [openEntry, setOpenEntry] = useState(null);
  const { show } = useError();

  const openEntryFor = async (auditRow) => {
    try {
      const entry = await fetchEntry(auditRow.target_id);
      setOpenEntry(entry);
    } catch (err) {
      show(err.message || 'Could not open entry — it may have been deleted.');
    }
  };

  return (
    <div>
      <div className="range-bar">
        <select className="field-select" value={actionFilter} onChange={e => setActionFilter(e.target.value)}>
          <option value="">All actions</option>
          <option value="entry.">Entry edits</option>
          <option value="user.">User changes</option>
          <option value="job.">Job changes</option>
          <option value="doc.">Document changes</option>
          <option value="jha_template.">JHA template</option>
          <option value="pay.">Pay run actions</option>
        </select>
        <button className="btn-sm" onClick={() => refetch()}>Refresh</button>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>When</th><th>Actor</th><th>Action</th>
            <th>Target</th><th>Reason</th><th></th>
          </tr>
        </thead>
        <tbody>
          {entries.map(e => (
            <Fragment key={e.id}>
              <tr>
                <td>{new Date(e.at).toLocaleString()}</td>
                <td>{e.actor_name || '—'}</td>
                <td><span className="badge">{e.action}</span></td>
                <td style={{ fontFamily: 'var(--font-label)', fontSize: 11, color: 'var(--warm-gray)' }}>
                  {e.target_table}{e.target_id ? ` · ${e.target_id.slice(0, 8)}` : ''}
                </td>
                <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.reason || ''}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {e.target_table === 'clock_entries' && e.action === 'entry.update' && e.target_id && (
                    <button className="btn-sm" onClick={() => openEntryFor(e)} style={{ marginRight: 6 }}>
                      Open
                    </button>
                  )}
                  <button className="btn-sm" onClick={() => setExpanded(expanded === e.id ? null : e.id)}>
                    {expanded === e.id ? 'Hide' : 'Diff'}
                  </button>
                </td>
              </tr>
              {expanded === e.id && (
                <tr>
                  <td colSpan="6" style={{ background: 'var(--surface)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <pre style={diffStyle}>before:{'\n'}{JSON.stringify(e.before_json, null, 2)}</pre>
                      <pre style={diffStyle}>after:{'\n'}{JSON.stringify(e.after_json, null, 2)}</pre>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
      {openEntry && (
        <EntryEditModal entry={openEntry} onClose={() => { setOpenEntry(null); refetch(); }} />
      )}
    </div>
  );
}
